import axios from '@axios';
import _ from '@lodash';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import settingsConfig from 'app/configs/settingsConfig';
import { showMessage } from 'app/store/fuse/messageSlice';
import { setInitialSettings } from 'app/store/fuse/settingsSlice';
import jwtService from '../auth/services/jwtService';
import jwtServiceConfig from '../auth/services/jwtService/jwtServiceConfig';
import { apiRoutes } from '@/app/apiConfig/apiRoutes';
import { changeFuseTheme } from 'app/store/fuse/settingsSlice.js';

export const getUser = createAsyncThunk('user/getUser', async (prop, { dispatch }) => {
  const user = await axios.get(jwtServiceConfig.getUser);

  if (!prop?.ignoreTheme) {
    const theme = await dispatch(getUserTheme(user.data.licenseeId)).unwrap();
    dispatch(changeFuseTheme(theme));
  }

  if (user.loginRedirectUrl) {
    settingsConfig.loginRedirectUrl = user.loginRedirectUrl; // for example 'apps/academy'
  }

  return user.data;
});

export const getUserLogo = createAsyncThunk(
  'user/getUserLogo',
  async (userId, { rejectWithValue }) => {
    try {
      const userLogo = await axios.get(`${apiRoutes.users}/${userId}/logo`);
      return userLogo.data.photoURL;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getUserTheme = createAsyncThunk(
  'user/theme',
  async (licenseeId, { rejectWithValue }) => {
    try {
      const userLogo = await axios.get(`${apiRoutes.licensees}/${licenseeId}/config`);
      return userLogo.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateUserSettings = createAsyncThunk(
  'user/updateSettings',
  async (settings, { dispatch, getState }) => {
    const { user } = getState();
    const newUser = _.merge({}, user, { data: { settings } });
    dispatch(updateUserData(newUser));

    return newUser;
  },
);

export const updateUserShortcuts = createAsyncThunk(
  'user/updateShortucts',
  async (shortcuts, { dispatch, getState }) => {
    const { user } = getState();
    const newUser = {
      ...user,
      data: {
        ...user,
        shortcuts,
      },
    };

    dispatch(updateUserData(newUser));

    return newUser;
  },
);

export const updateUserData = (user) => async (dispatch, getState) => {
  if (!user.role.name || user.role.length === 0) {
    // is guest
    return;
  }

  jwtService
    .updateUserData(user)
    .then(() => {
      dispatch(showMessage({ message: 'User data saved with api' }));
    })
    .catch((error) => {
      dispatch(showMessage({ message: error.message }));
    });
};

const initialState = {};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUserState: (state) => {
      setInitialSettings();
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateUserSettings.fulfilled, (state, action) => action.payload)
      .addCase(updateUserShortcuts.fulfilled, (state, action) => action.payload)
      .addCase(getUser.fulfilled, (state, action) => action.payload)
      .addCase(getUserLogo.fulfilled, (state, action) => {
        state.photoURL = action.payload;
        return state;
      })
      .addCase(getUserTheme.fulfilled, (state, action) => {
        state.theme = action.payload;
        return state;
      });
  },
});

export const { resetUserState } = userSlice.actions;

export const selectUser = ({ user }) => user;

export const selectUserShortcuts = ({ user }) => user.shortcuts;

export default userSlice.reducer;
